import * as ol from "ol";
import * as Extent from "ol/extent";
import * as Geom from "ol/geom";
import * as Interaction from "ol/interaction";
import { Tile as TileLayer, Vector as VectorLayer } from "ol/layer";
import "ol/ol.css";
import * as Proj from "ol/proj";
import { OSM as OsmSource, Vector as VectorSource } from "ol/source";
import * as Style from "ol/style";

import * as React from "react";
import { Async as AsyncSelect, default as Select } from "react-select";

import { Area, AreaWorking } from "../../entities/Area";

import { Styles } from "react-select/lib/styles";
import { ActionMeta, ValueType } from "react-select/lib/types";
import { PresetObj } from "../../api-entities/PresetArea";
import { useMapAttachedState } from "../../hooks/useMapAttachedState";
import { useOlFeatureEditing } from "../../hooks/useOlFeatureEditing";
import { findFinestGrid, snapAreaToGrid } from "../../utils/Area";
import { validateArea } from "../../validation/Validators";
import { AreaEditor } from "./AreaEditor";
import { ProductLeg } from "../../entities/ProductLeg";


const maxArea = { north: 90, south: -90, west: -180, east: 180 };

const AREA_INDEX = "areaIndex";
const INACTIVE_STYLE = "inactiveStyle";

const colors: Array<[number, number, number]> = [
  [255, 200, 0],
  [255, 0, 200],
  [0, 220, 210],
  [100, 230, 100],
  [255, 60, 70],
  [70, 90, 255]
];

const baseAreaStyle = new Style.Style({
  stroke: new Style.Stroke({ color: "blue", width: 2 }),
  fill: new Style.Fill({ color: [0, 0, 0, 0] })
});

const modifyStyleInvalid = (style: Style.Style) => {
  const newStyle = style.clone();
  newStyle.getStroke().setColor("red");
  return newStyle;
};

const activeAreaStyle = new Style.Style({
  stroke: new Style.Stroke({ color: "green", width: 3 }),
  fill: new Style.Fill({ color: [0, 0, 0, 0] })
});

const draggingAreaStyle = new Style.Style({
  stroke: new Style.Stroke({ color: "green", width: 3, lineDash: [18, 6] }),
  fill: new Style.Fill({ color: [0, 0, 0, 0] })
});

const [leftMost, , rightMost] = [...Proj.fromLonLat([-180, 90]), ...Proj.fromLonLat([180, -90])];

function areaIsComplete(area: AreaWorking): area is Area {
  return (
    area.east !== undefined &&
    area.west !== undefined &&
    area.north !== undefined &&
    area.south !== undefined
  );
}

// Convert an Area to a corresponding OpenLayers Polygon, respecting areas that cross
// the date-line (i.e. west > east)
const areaToGeometry = (a: Area) => {
  const [x1, top] = Proj.fromLonLat([a.west, a.north]);
  const [x2, bottom] = Proj.fromLonLat([a.east, a.south]);

  const [left, right] = x1 < x2 ? [x1, x2] : [x1, x2 + rightMost - leftMost];

  return new Geom.Polygon([
    [
      [left, top],
      [right, top],
      [right, bottom],
      [left, bottom]
    ]
  ]);
};

export type AreaAction =
  | { type: "create"; area: Area }
  | { type: "update"; area: Area; index: number }
  | { type: "delete"; index: number };

export type AreaValidationProblem = {
  field: string;
  message: string;
};

type OwnProps<T> = {
  readonly?: boolean;
  // The collection of entities on which the editor operates. Each contains a single geographic region
  areaEntities: T[];
  // Function to project each entity to its geographic region
  projectArea: (entity: T) => Area;
  // A set of current validation problems with the fields of each entity
  validationProblems?: AreaValidationProblem[][];
  // Event handler for user actions to create/update/delete on the geographic portion of the entities
  handleAction: (action: AreaAction) => void;
  // The product legs, when in the context of a product configuration (used for snapping to grids)
  legs?: Map<number, ProductLeg>;
  // A function to give a set of geographic area suggestions matching a user text input
  suggestArea?: (input: string) => Promise<PresetObj[]>;
  // Event handler to fire when the coordinate fields lose focus
  onBlur: () => void;
  // A library of frequently used areas the user has defined for their convenience
  userPresetAreas?: PresetObj[];
  // When true, a validation problem requires that the first input field should take the focus
  needsFocus: boolean;
  // Called after needsFocus caused a field to gain focus
  acknowledgeFocusRequest: () => void;
  // An editor supplied by the parent component for the non-area fields of the entity type.
  children?: (activeIndex: number | null) => React.ReactNode;
};

const showAreaInView = (view: ol.View, area: Area) => {
  view.fit(areaToGeometry(area).getExtent(), {
    padding: [20, 20, 20, 20],
    duration: 500,
    maxZoom: view.getZoom()
  });
};

const AreasEditor = <T extends unknown>(props: OwnProps<T>) => {
  const {
    areaEntities,
    projectArea,
    validationProblems: validationProblemsRaw,
    handleAction,
    legs: legsRaw,
    suggestArea,
    onBlur,
    userPresetAreas,
    needsFocus,
    acknowledgeFocusRequest,
    readonly,
    children
  } = props;

  const validationProblems = React.useMemo(() => validationProblemsRaw || [], [
    validationProblemsRaw
  ]);
  const legs = React.useMemo(() => legsRaw || new Map<number, ProductLeg>(), [legsRaw]);

  const areas = React.useMemo(() => areaEntities.map(projectArea), [areaEntities, projectArea]);

  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const mapRef = React.useRef<ol.Map | null>(null);
  const areasLayerRef = React.useRef<VectorLayer | null>(null);
  const newAreaFeatureRef = React.useRef<ol.Feature>();

  const [activeAreaRaw, setActiveArea] = React.useState<number | null>(null);
  const [addMode, setAddMode] = React.useState(false);

  const [snapLayersVisible, setSnapLayerVisible] = useMapAttachedState(legs, () => true);

  const [snappingLayers, setSnappingLayers] = React.useState(new Map<number, VectorLayer>());
  const [features, setFeatures] = React.useState<ol.Feature[]>();
  const [snappingFeatures, setSnappingFeatures] = React.useState(new Map<number, ol.Feature[]>());

  const [draftArea, setDraftArea] = React.useState<AreaWorking>({});

  // activeAreaRaw may fall outside the range after areas are deleted
  const activeArea = React.useMemo(() => {
    if (activeAreaRaw === null || areas.length === 0) {
      return null;
    } else {
      return Math.min(areas.length - 1, activeAreaRaw);
    }
  }, [areas, activeAreaRaw]);

  // Pre-calculate the polygon representation of the areas
  const areaGeometries = React.useMemo(() => {
    return areas.map(areaToGeometry);
  }, [areas]);

  // Create the map element, controls, and vector layer
  React.useLayoutEffect(() => {
    mapRef.current = new ol.Map({
      target: mapContainerRef.current!,
      layers: [
        new TileLayer({
          source: new OsmSource()
        })
      ],
      view: new ol.View({
        center: [0, 0],
        zoom: 1,
        minZoom: 1
      }),
      interactions: Interaction.defaults({
        dragPan: false // Disabled: the Feature Editing hook handles dragging instead
      }),
      controls: [],
      moveTolerance: 5
    });

    const map = mapRef.current;

    // The map's concept of its own size is often wrong without this
    map.once("rendercomplete", () => {
      map.updateSize();
    });

    areasLayerRef.current = new VectorLayer({
      source: new VectorSource({
        features: []
      }),
      style: baseAreaStyle,
      zIndex: 1
    });

    map.addLayer(areasLayerRef.current);

    const datelineLayer = new VectorLayer({
      source: new VectorSource({
        features: [
          new ol.Feature({
            geometry: new Geom.LineString([Proj.fromLonLat([180, 90]), Proj.fromLonLat([180, -90])])
          })
        ]
      }),
      style: new Style.Style({
        stroke: new Style.Stroke({ color: "gray", width: 1, lineDash: [6, 2] })
      })
    });
    map.addLayer(datelineLayer);

    return () => {
      map.setTarget("");
    };
  }, []);

  // Create additional layers for the snapped areas
  React.useLayoutEffect(() => {
    let colourIndex = 0;
    const newSnappingLayers = new Map<number, VectorLayer>();

    for (const [legId] of legs) {
      const layer = new VectorLayer({
        source: new VectorSource({
          features: []
        }),
        style: new Style.Style({
          stroke: new Style.Stroke({ color: [0, 0, 0, 0], width: 0 }),
          fill: new Style.Fill({ color: [...colors[colourIndex], 0.3] })
        })
      });

      newSnappingLayers.set(legId, layer);
      mapRef.current!.addLayer(layer);
      colourIndex++;
    }

    setSnappingLayers(newSnappingLayers);

    return () => {
      newSnappingLayers.forEach(l => mapRef.current!.removeLayer(l));
    };
  }, [legs]);

  // Show or hide snapping layers
  React.useLayoutEffect(() => {
    snapLayersVisible.forEach((visible, legId) => {
      const layer = snappingLayers.get(legId);
      if (layer) {
        layer.setVisible(visible);
      }
    });
  }, [snapLayersVisible, snappingLayers]);

  // Clicking on an inactive feature makes the corresponding area the active one (the one
  // that can be edited)
  const mapClickHandler = React.useCallback(
    (e: ol.MapBrowserEvent) => {
      const feature = (
        mapRef.current!.getFeaturesAtPixel(e.pixel, {
          hitTolerance: 5,
          layerFilter: l => l === areasLayerRef.current
        }) || []
      ).find(f => f.get(AREA_INDEX) !== activeArea);

      if (feature) {
        const areaIndex = feature.get(AREA_INDEX);
        setActiveArea(areaIndex);
        setAddMode(false);
      } else {
        setActiveArea(null);
      }
    },
    [activeArea, setActiveArea, setAddMode]
  );

  React.useEffect(() => {
    mapRef.current!.on("click", mapClickHandler);
    return () => mapRef.current!.un("click", mapClickHandler);
  }, [mapClickHandler]);

  // Convert areas to features and add an as-yet undrawn feature for use when drawing new areas
  const viewInitializedRef = React.useRef(false);
  React.useEffect(() => {
    const newFeatures = areaGeometries.map((g, i) => {
      const feature = new ol.Feature({
        geometry: g
      });
      if (validationProblems.length > i && validationProblems[i].length > 0) {
        feature.setStyle(modifyStyleInvalid(baseAreaStyle));
      }
      feature.set(AREA_INDEX, i);
      feature.set(INACTIVE_STYLE, feature.getStyle());
      return feature;
    });

    const areasSource = areasLayerRef.current!.getSource();

    areasSource.addFeatures(newFeatures);
    setFeatures(newFeatures);

    newAreaFeatureRef.current = new ol.Feature();
    areasSource.addFeature(newAreaFeatureRef.current);

    // When the component mounts, zoom the map to fit the existing areas
    if (!viewInitializedRef.current) {
      if (areaGeometries.length > 0) {
        mapRef.current!.once("rendercomplete", () => {
          let extent = Extent.createEmpty();
          areaGeometries.forEach(g => {
            extent = Extent.extend(extent, g.getExtent());
          });
          mapRef.current!.getView().fit(extent, { padding: [20, 20, 20, 20], duration: 500 });
        });
      }
      viewInitializedRef.current = true;
    }

    return () => areasSource.clear();
  }, [areaGeometries, validationProblems]);

  // Draw the snapped areas (but omit the active feature and save refs)
  React.useEffect(() => {
    const newFeatureMap = new Map<number, ol.Feature[]>();

    if (snappingLayers.size === 0) {
      return;
    }

    for (const [legId, leg] of legs) {
      const newFeatures = areas.map(a => {
        const snappedArea = leg.grid && leg.grid.value ? snapAreaToGrid(a, leg.grid.value) : a;

        const feature = new ol.Feature({
          geometry: areaToGeometry(snappedArea)
        });

        return feature;
      });

      const featureSource = snappingLayers.get(legId)!.getSource();
      featureSource.addFeatures(newFeatures);
      newFeatureMap.set(legId, newFeatures);
    }

    setSnappingFeatures(newFeatureMap);

    return () => {
      for (const layer of snappingLayers.values()) {
        layer.getSource().clear();
      }
    };
  }, [areas, legs, snappingLayers]);

  // Handles the end-of-drag events for the useOlFeatureEditing hook
  const featureUpdateHandler = React.useCallback(
    (area: Area, feature?: ol.Feature) => {
      if (feature === undefined) {
        handleAction({ type: "create", area });
        setActiveArea(areas.length);
        setAddMode(false);
      } else {
        handleAction({ type: "update", area, index: feature.get(AREA_INDEX) });
      }
    },
    [areas, handleAction, setActiveArea, setAddMode]
  );

  const activeFeature = React.useMemo(
    () => (features && activeArea !== null ? features[activeArea] : undefined),
    [features, activeArea]
  );

  const snappingGrid = React.useMemo(() => findFinestGrid(legs), [legs]);

  const featureEditState = useOlFeatureEditing({
    mapRef,
    editableLayer: areasLayerRef,
    addMode,
    activeFeature,
    onUpdateFeature: featureUpdateHandler,
    grid: snappingGrid
  });

  // Draw the active feature, either being dragged or simply highlighted
  React.useEffect(() => {
    if (featureEditState.coords === undefined) {
      if (activeArea !== null && activeFeature) {
        activeFeature.setGeometry(areaGeometries[activeArea]);
        activeFeature.setStyle(activeAreaStyle);
      }
    } else {
      const coords = featureEditState.coords;
      const [coord1, coord2] = coords;

      const targetFeature = activeFeature || newAreaFeatureRef.current!;

      targetFeature.setGeometry(
        new Geom.Polygon([[coord1, [coord1[0], coord2[1]], coord2, [coord2[0], coord1[1]]]])
      );

      targetFeature.setStyle(draggingAreaStyle);
    }

    return activeFeature
      ? () => {
        activeFeature.setStyle(activeFeature.get(INACTIVE_STYLE));
      }
      : () => newAreaFeatureRef.current!.setGeometry(undefined);
  }, [areaGeometries, activeArea, featureEditState, activeFeature]);

  // Draw the snapped areas for the active feature
  React.useEffect(() => {
    let generatingArea: Area | undefined;

    if (featureEditState.area !== null) {
      generatingArea = featureEditState.area;
    } else {
      if (activeArea !== null) {
        generatingArea = areas[activeArea];
      }
    }

    if (generatingArea !== undefined && activeArea !== null) {
      for (const [legId, leg] of legs) {
        const snappedArea =
          leg.grid && leg.grid.value && leg.grid.value !== snappingGrid
            ? snapAreaToGrid(generatingArea, leg.grid.value)
            : generatingArea;
        const featureSet = snappingFeatures.get(legId);
        if (featureSet && featureSet.length > activeArea) {
          featureSet[activeArea].setGeometry(areaToGeometry(snappedArea));
        }
      }
    }
  }, [areas, activeArea, featureEditState, legs, snappingFeatures, snappingGrid]);

  // Style snap selection legend with a dynamically created stylesheet
  React.useEffect(() => {
    if (legs.size > 1) {
      const sheetElement = document.createElement("style");
      sheetElement.appendChild(document.createTextNode(""));
      document.head.appendChild(sheetElement);
      const sheet = sheetElement.sheet as CSSStyleSheet;

      const colorToCss = (r: number, g: number, b: number) => `rgba(${r}, ${g}, ${b}, 1)`;

      let index = 0;
      legs.forEach(() => {
        const color = colors[index++];
        const colorCss = colorToCss(...color);
        const contrastCss = color[0] + color[1] > 255 ? "black" : "white";

        sheet.insertRule(
          `.map__legend .checkbox__multiple-choice:nth-of-type(${index}) input[type="checkbox"] + label:before { content: ''; background-color: ${colorCss} !important; border-color: ${colorCss} !important; }`
        );
        sheet.insertRule(
          `.map__legend .checkbox__multiple-choice:nth-of-type(${index}) input[type="checkbox"] + label:after { content: ''; border-left-color: ${contrastCss}; border-bottom-color: ${contrastCss}; }`
        );
      });

      return () => {
        document.head.removeChild(sheetElement);
      };
    }
  }, [legs]);

  // Render, where applicable, check boxes to show the snapped areas
  const snapLegend = React.useMemo(
    () =>
      renderSnapLegend(legs, snapLayersVisible, (legId, visible) =>
        setSnapLayerVisible(legId, visible)
      ),
    [legs, snapLayersVisible, setSnapLayerVisible]
  );

  const enableAddModeHandler = React.useCallback(() => {
    const newAddMode = !addMode;
    if (newAddMode) {
      setActiveArea(null);
    }
    setAddMode(newAddMode);
  }, [addMode, setAddMode]);

  const deleteAreaHandler = React.useCallback(() => {
    if (activeArea !== null) {
      handleAction({ type: "delete", index: activeArea });
    }
  }, [handleAction, activeArea]);

  const areaEditorChangeHandler = React.useCallback(
    (area: AreaWorking) => {
      if (activeArea === null) {
        setDraftArea(area);
        return;
      }
      if (areaIsComplete(area)) {
        handleAction({ type: "update", area, index: activeArea });
      }
    },
    [activeArea, handleAction]
  );

  const addDraftArea = React.useCallback(() => {
    if (areaIsComplete(draftArea)) {
      const result = validateArea(draftArea);
      if (result.isValid) {
        handleAction({ type: "create", area: draftArea });
        setDraftArea({});
        showAreaInView(mapRef.current!.getView(), draftArea);
      } else {
        alert(result.error);
      }
    }
  }, [draftArea, setDraftArea, handleAction]);

  const reactSelectStyles: Partial<Styles> = {
    container: p => ({ ...p, margin: "0.4rem" }),
    control: p => ({ ...p, minHeight: "32px" })
  };

  return (
    <div className="map">
      <div
        ref={mapContainerRef}
        className="map__openlayers"
        style={{ cursor: addMode ? "crosshair" : featureEditState.cursor }}
      ></div>
      <div className="map__controls">
        {
          !readonly &&
          <div className="map__control-group">
            {userPresetAreas && userPresetAreas.length > 0 && (
              <Select<PresetObj>
                getOptionLabel={option => option.name}
                options={userPresetAreas}
                controlShouldRenderValue={false}
                styles={reactSelectStyles}
                isOptionSelected={() => false}
                onChange={(value: ValueType<PresetObj>, action: ActionMeta) => {
                  if (action.action === "select-option") {
                    if (value) {
                      const newArea = (value as PresetObj).area;
                      handleAction({ type: "create", area: newArea });
                      showAreaInView(mapRef.current!.getView(), newArea);
                    }
                  }
                }}
                placeholder="Add preset area"
              />
            )}

            {suggestArea && (
              <AsyncSelect<PresetObj>
                loadOptions={suggestArea}
                styles={reactSelectStyles}
                getOptionLabel={option => option.name}
                controlShouldRenderValue={false}
                isOptionSelected={() => false}
                onChange={(value, action) => {
                  if (action.action === "select-option") {
                    if (value) {
                      const newArea = (value as PresetObj).area;
                      handleAction({ type: "create", area: newArea });
                      showAreaInView(mapRef.current!.getView(), newArea);
                    }
                  }
                }}
                placeholder="Search and add..."
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null
                }}
              />
            )}
            <button
              type="button"
              className={"btn btn__compact btn__main" + (addMode ? " map__add-mode" : "")}
              onClick={enableAddModeHandler}
            >
              Draw new area
            </button>
            <button
              type="button"
              className="btn btn__compact btn__main"
              onClick={deleteAreaHandler}
              disabled={activeArea === null}
            >
              Delete area
            </button>
          </div>
        }
        {snapLegend}
        {children && <div className="map__control-group">{children(activeArea)}</div>}
        <div className="map__control-group">
          <AreaEditor
            title={
              featureEditState.area || activeArea !== null ? "Selected area" : "Input new area"
            }
            selectedArea={
              featureEditState.area || (activeArea !== null ? areas[activeArea] : draftArea)
            }
            maxArea={maxArea}
            onChange={areaEditorChangeHandler}
            onBlur={onBlur}
            needsFocus={needsFocus}
            acknowledgeFocusRequest={acknowledgeFocusRequest}
            grid={snappingGrid}
            readonly={readonly}
          />
        </div>
        {featureEditState.area == null && activeArea == null && (
          <button
            type="button"
            className="btn btn__compact btn__main"
            disabled={!areaIsComplete(draftArea)}
            onClick={addDraftArea}
          >
            Add to map
          </button>
        )}

        {activeArea !== null &&
        validationProblems.length > activeArea &&
        validationProblems[activeArea].map((p, i) => (
          <label key={i} className="error">
            {p.field + ": "}
            {p.message}
          </label>
        ))}
      </div>
    </div>
  );
};

const renderSnapLegend = (
  legs: Map<number, ProductLeg>,
  visibility: Map<number, boolean>,
  setVisibility: (legId: number, newVisibility: boolean) => void
) => {
  let dummyId = 0;
  const nextId = (prefix: string) => `${prefix}-${dummyId++}`;
  // Render snap selection legend

  if (legs.size > 1) {
    const layerBoxes = Array.from(legs, ([legId]) => {
      const id = nextId("snap-layer-box");
      return (
        <div className="checkbox__multiple-choice" key={id}>
          <input
            id={id}
            type="checkbox"
            checked={visibility.get(legId) === false ? false : true}
            onChange={e => {
              setVisibility(legId, e.target.checked);
            }}
          />
          <label htmlFor={id}>
            <span>Leg {legId + 1}</span>
          </label>
        </div>
      );
    });
    return (
      <div className="map__legend map__control-group">
        <fieldset>
          <legend className="order__subtitle" aria-label="Show or hide grid-snapped areas">
            Show snapping
          </legend>
          {layerBoxes}
        </fieldset>
      </div>
    );
  }
};

export default AreasEditor;
